import React, { useState, useEffect, useCallback } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { perDiemAPI, PerDiemEntry, PerDiemEntryCreate, PerDiemRates } from '../../services/api';
import { useProjectStore } from '../../store/projectStore';
import { useClientStore } from '../../store/clientStore';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';

const inputClass =
  'w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent';

export const PerDiemSection: React.FC = () => {
  const { projects, fetchProjects } = useProjectStore();
  const { clients, fetchClients } = useClientStore();

  const [entries, setEntries] = useState<PerDiemEntry[]>([]);
  const [rates, setRates] = useState<PerDiemRates | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState<{
    project_id: string;
    destination: string;
    start_date: string;
    end_date: string;
    meals_rate: number;
    lodging_rate: number;
    is_first_day_partial: boolean;
    is_last_day_partial: boolean;
    notes: string;
  }>({
    project_id: '',
    destination: '',
    start_date: '',
    end_date: '',
    meals_rate: 0,
    lodging_rate: 0,
    is_first_day_partial: false,
    is_last_day_partial: false,
    notes: '',
  });

  const fetchEntries = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await perDiemAPI.list();
      setEntries(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch per diem entries');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEntries();
    fetchProjects();
    fetchClients();
    perDiemAPI.getRates().then(setRates).catch(() => {});
  }, [fetchEntries, fetchProjects, fetchClients]);

  const destinations = rates ? Object.keys(rates.destinations).sort() : [];

  // Group projects by client
  const projectsByClient = React.useMemo(() => {
    const grouped: Record<string, { clientName: string; projects: typeof projects }> = {};
    for (const project of projects) {
      const clientId = project.client_id;
      if (!grouped[clientId]) {
        const client = clients.find((c) => c.id === clientId);
        grouped[clientId] = {
          clientName: project.client_name || client?.name || 'Unknown Client',
          projects: [],
        };
      }
      grouped[clientId].projects.push(project);
    }
    return Object.values(grouped).sort((a, b) => a.clientName.localeCompare(b.clientName));
  }, [projects, clients]);

  const handleDestinationChange = (destination: string) => {
    const destRates = rates?.destinations[destination];
    setFormData((prev) => ({
      ...prev,
      destination,
      meals_rate: destRates?.meals ?? prev.meals_rate,
      lodging_rate: destRates?.lodging ?? prev.lodging_rate,
    }));
  };

  const resetForm = () => {
    setFormData({
      project_id: '',
      destination: '',
      start_date: '',
      end_date: '',
      meals_rate: 0,
      lodging_rate: 0,
      is_first_day_partial: false,
      is_last_day_partial: false,
      notes: '',
    });
  };

  const openModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      const payload: PerDiemEntryCreate = {
        project_id: formData.project_id,
        destination: formData.destination,
        start_date: formData.start_date,
        end_date: formData.end_date,
        meals_rate: formData.meals_rate,
        lodging_rate: formData.lodging_rate,
        is_first_day_partial: formData.is_first_day_partial || undefined,
        is_last_day_partial: formData.is_last_day_partial || undefined,
        notes: formData.notes.trim() || undefined,
      };
      await perDiemAPI.create(payload);
      setIsModalOpen(false);
      resetForm();
      await fetchEntries();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create per diem entry');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this per diem entry?')) return;
    try {
      await perDiemAPI.delete(id);
      setEntries((prev) => prev.filter((e) => e.id !== id));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete per diem entry');
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  const formatDate = (dateStr: string) =>
    new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Per Diem Entries</h2>
        <Button onClick={openModal} size="sm">
          <PlusIcon className="h-4 w-4 mr-1.5" />
          Add Per Diem
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">Loading per diem entries...</div>
      )}

      {/* Empty State */}
      {!isLoading && entries.length === 0 && (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <p className="text-lg mb-2">No per diem entries yet</p>
          <p className="text-sm">Click "Add Per Diem" to create your first entry.</p>
        </div>
      )}

      {/* Entries List */}
      {!isLoading && entries.length > 0 && (
        <div className="space-y-3">
          {entries.map((entry) => (
            <div
              key={entry.id}
              className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <span className="font-medium text-gray-900 dark:text-white truncate">
                    {entry.destination}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {entry.num_days} {entry.num_days === 1 ? 'day' : 'days'}
                  </span>
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {formatDate(entry.start_date)} &ndash; {formatDate(entry.end_date)}
                </div>
              </div>
              <div className="flex items-center gap-4 ml-4">
                <span className="text-lg font-semibold text-green-600 dark:text-green-400 whitespace-nowrap">
                  {formatCurrency(entry.total_amount)}
                </span>
                <button
                  onClick={() => handleDelete(entry.id)}
                  className="p-1.5 text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 transition-colors"
                  title="Delete entry"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="New Per Diem Entry" size="lg">
        <form onSubmit={handleCreate} className="space-y-4">
          {/* Project Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Project
            </label>
            <select
              required
              value={formData.project_id}
              onChange={(e) => setFormData((prev) => ({ ...prev, project_id: e.target.value }))}
              className={inputClass}
            >
              <option value="">Select a project</option>
              {projectsByClient.map((group) => (
                <optgroup key={group.clientName} label={group.clientName}>
                  {group.projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Destination */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Destination
            </label>
            <select
              required
              value={formData.destination}
              onChange={(e) => handleDestinationChange(e.target.value)}
              className={inputClass}
            >
              <option value="">Select a destination</option>
              {destinations.map((dest) => (
                <option key={dest} value={dest}>
                  {dest}
                </option>
              ))}
            </select>
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Start Date
              </label>
              <input
                type="date"
                required
                value={formData.start_date}
                onChange={(e) => setFormData((prev) => ({ ...prev, start_date: e.target.value }))}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                End Date
              </label>
              <input
                type="date"
                required
                value={formData.end_date}
                onChange={(e) => setFormData((prev) => ({ ...prev, end_date: e.target.value }))}
                className={inputClass}
              />
            </div>
          </div>

          {/* Rates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Meals Rate ($/day)
              </label>
              <input
                type="number"
                required
                min={0}
                step={0.01}
                value={formData.meals_rate}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, meals_rate: parseFloat(e.target.value) || 0 }))
                }
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Lodging Rate ($/night)
              </label>
              <input
                type="number"
                required
                min={0}
                step={0.01}
                value={formData.lodging_rate}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, lodging_rate: parseFloat(e.target.value) || 0 }))
                }
                className={inputClass}
              />
            </div>
          </div>

          {/* Partial Day Toggles */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="first_day_partial"
                checked={formData.is_first_day_partial}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, is_first_day_partial: e.target.checked }))
                }
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 dark:border-gray-600 rounded"
              />
              <label htmlFor="first_day_partial" className="text-sm text-gray-700 dark:text-gray-300">
                First day is partial (travel day)
              </label>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="last_day_partial"
                checked={formData.is_last_day_partial}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, is_last_day_partial: e.target.checked }))
                }
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 dark:border-gray-600 rounded"
              />
              <label htmlFor="last_day_partial" className="text-sm text-gray-700 dark:text-gray-300">
                Last day is partial (travel day)
              </label>
            </div>
            {(formData.is_first_day_partial || formData.is_last_day_partial) && (
              <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                Per GSA 75% rule: partial (travel) days are reimbursed at 75% of the meals rate.
              </p>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
              rows={3}
              placeholder="Optional notes..."
              className={inputClass}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" isLoading={isSubmitting} disabled={isSubmitting}>
              Create Entry
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

